import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { intakeFormService } from '../services/api';
import '../styles/IntakeFormPage.css';

// Public, no-login intake form -- shared with the client via a
// /intake/{client_token} link generated when their investigation is created
// (see routers/investigations.py:create_investigation). Deliberately styled
// distinctly from the rest of the app: no sidebar, no tab bar, no app chrome
// at all -- this is the one screen in the whole product a non-technical
// client on the other end of a scam ever sees.
//
// ONE-SHOT SUBMISSION: once submitted, this form is permanently read-only for
// the client (server enforces this -- see IntakeForm.status in models.py and
// the 400 returned by POST /intake/{token}/submit if status isn't 'pending').
// Only the investigating team can edit anything after that point.

const EMPTY_FORM = {
  client_name: '', client_email: '', client_phone: '', client_country: '',
  scam_start_date: '', scam_end_date: '', total_loss_amount: '', total_loss_currency: '',
  fake_platform_name: '', scammer_usernames: '', initial_contact_method: '',
  police_report_filed: false, police_department: '', police_case_number: '',
  police_report_date: '', cybercrime_reference: '', assigned_officer_name: '',
  narrative: '',
  evidence_identity_docs: false, evidence_identity_notes: '',
  evidence_blockchain: false, evidence_txids: '', evidence_scammer_addresses: '', evidence_client_addresses: '',
  evidence_communications: false, evidence_communications_notes: '',
  evidence_financial_proof: false, evidence_financial_notes: '',
  disclaimer_accepted: false, signature_name: '',
};

function Field({ label, hint, children }) {
  return (
    <div className="intake-field">
      <label>{label}</label>
      {hint && <p className="intake-field-hint">{hint}</p>}
      {children}
    </div>
  );
}

function Section({ number, title, subtitle, children }) {
  return (
    <div className="intake-section">
      <div className="intake-section-header">
        <span className="intake-section-num">{number}</span>
        <div>
          <h2>{title}</h2>
          {subtitle && <p>{subtitle}</p>}
        </div>
      </div>
      <div className="intake-section-body">{children}</div>
    </div>
  );
}

function EvidenceCheck({ checked, onChange, label, notesValue, onNotesChange, notesPlaceholder }) {
  return (
    <div className="intake-evidence-item">
      <label className="intake-checkbox-row">
        <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} />
        <span>{label}</span>
      </label>
      {checked && (
        <textarea
          className="intake-evidence-notes"
          placeholder={notesPlaceholder}
          value={notesValue || ''}
          onChange={e => onNotesChange(e.target.value)}
          rows={2}
        />
      )}
    </div>
  );
}

export default function IntakeFormPage() {
  const { token } = useParams();
  const [loading, setLoading]   = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [locked, setLocked]     = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm]         = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]       = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await intakeFormService.public.get(token);
      setLocked(!!res.data.locked);
      if (res.data.locked) {
        // Already submitted -- nothing left for the client to do here.
        setSubmitted(true);
      } else {
        setForm(f => ({ ...f, ...Object.fromEntries(
          Object.keys(EMPTY_FORM).map(k => [k, res.data[k] ?? EMPTY_FORM[k]])
        )}));
      }
    } catch (e) {
      if (e.response?.status === 404) setNotFound(true);
      else setError('Failed to load the form. Please refresh and try again, or contact your investigator.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const set = (key, value) => setForm(f => ({ ...f, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.disclaimer_accepted) {
      setError('Please read and accept the confidentiality & disclaimer statement before submitting.');
      return;
    }
    if (!form.signature_name.trim()) {
      setError('Please type your full name to sign the form before submitting.');
      return;
    }
    try {
      setSubmitting(true);
      const payload = { ...form };
      // Empty numeric string -> null, so the backend doesn't choke on ''
      if (payload.total_loss_amount === '') payload.total_loss_amount = null;
      else payload.total_loss_amount = Number(payload.total_loss_amount);
      await intakeFormService.public.submit(token, payload);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="intake-page"><div className="intake-loading">Loading your form…</div></div>;
  }

  if (notFound) {
    return (
      <div className="intake-page">
        <div className="intake-card intake-status-card">
          <h1>Form not found</h1>
          <p>This link doesn't match an active intake form. Please double-check the link your
             investigator sent you, or reach out to them directly for a new one.</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="intake-page">
        <div className="intake-card intake-status-card">
          <div className="intake-status-icon">✓</div>
          <h1>Thank you — your information has been received</h1>
          <p>
            Your submission is now with the investigating team. If anything needs to be corrected
            or added, please contact your investigator directly — for security reasons this form
            can no longer be edited from your side.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="intake-page">
      <form className="intake-card" onSubmit={handleSubmit}>
        <header className="intake-header">
          <h1>Crypto Incident Intake Form</h1>
          <p>
            Please complete this form as thoroughly as you can. The more detail you provide now,
            the faster your investigator can start tracing what happened. You can only submit this
            form once, so take your time — but don't worry if you don't have every detail; your
            investigator can help fill in gaps afterward.
          </p>
        </header>

        {error && <div className="intake-error">⚠ {error}</div>}

        <Section number="1" title="Your Contact Information">
          <div className="intake-grid-2">
            <Field label="Full name"><input type="text" value={form.client_name} onChange={e => set('client_name', e.target.value)} /></Field>
            <Field label="Email address"><input type="email" value={form.client_email} onChange={e => set('client_email', e.target.value)} /></Field>
            <Field label="Phone number"><input type="tel" value={form.client_phone} onChange={e => set('client_phone', e.target.value)} /></Field>
            <Field label="Country"><input type="text" value={form.client_country} onChange={e => set('client_country', e.target.value)} /></Field>
          </div>
        </Section>

        <Section number="2" title="Incident Overview" subtitle="What happened, and roughly when.">
          <div className="intake-grid-2">
            <Field label="When did contact with the scammer begin?"><input type="date" value={form.scam_start_date} onChange={e => set('scam_start_date', e.target.value)} /></Field>
            <Field label="When did it end (or when did you realize)?"><input type="date" value={form.scam_end_date} onChange={e => set('scam_end_date', e.target.value)} /></Field>
            <Field label="Total amount lost"><input type="number" step="any" value={form.total_loss_amount} onChange={e => set('total_loss_amount', e.target.value)} /></Field>
            <Field label="Currency (e.g. USD, BTC, ETH)"><input type="text" value={form.total_loss_currency} onChange={e => set('total_loss_currency', e.target.value)} /></Field>
            <Field label="Name of the fake platform / app / website"><input type="text" value={form.fake_platform_name} onChange={e => set('fake_platform_name', e.target.value)} /></Field>
            <Field label="How were you first contacted?" hint="e.g. Instagram DM, WhatsApp, dating app, cold call"><input type="text" value={form.initial_contact_method} onChange={e => set('initial_contact_method', e.target.value)} /></Field>
          </div>
          <Field label="Scammer's username(s) / handle(s)" hint="One per line if there was more than one.">
            <textarea rows={2} value={form.scammer_usernames} onChange={e => set('scammer_usernames', e.target.value)} />
          </Field>
        </Section>

        <Section number="3" title="Law Enforcement & Official Reports" subtitle="Skip anything you haven't done yet — leave it blank.">
          <label className="intake-checkbox-row">
            <input type="checkbox" checked={form.police_report_filed} onChange={e => set('police_report_filed', e.target.checked)} />
            <span>I have filed a police report</span>
          </label>
          {form.police_report_filed && (
            <div className="intake-grid-2" style={{ marginTop: 10 }}>
              <Field label="Police department"><input type="text" value={form.police_department} onChange={e => set('police_department', e.target.value)} /></Field>
              <Field label="Case number"><input type="text" value={form.police_case_number} onChange={e => set('police_case_number', e.target.value)} /></Field>
              <Field label="Date filed"><input type="date" value={form.police_report_date} onChange={e => set('police_report_date', e.target.value)} /></Field>
              <Field label="Assigned officer's name"><input type="text" value={form.assigned_officer_name} onChange={e => set('assigned_officer_name', e.target.value)} /></Field>
            </div>
          )}
          <Field label="Cybercrime reference number" hint="e.g. IC3, Action Fraud, or your local equivalent">
            <input type="text" value={form.cybercrime_reference} onChange={e => set('cybercrime_reference', e.target.value)} />
          </Field>
        </Section>

        <Section number="4" title="Tell Us What Happened" subtitle="Write it in your own words — as much detail as you're comfortable sharing.">
          <textarea rows={8} className="intake-narrative" value={form.narrative} onChange={e => set('narrative', e.target.value)}
            placeholder="Start from the beginning: how you were approached, what you were told, when you started to suspect something was wrong…" />
        </Section>

        <Section number="5" title="Evidence Checklist" subtitle="Check anything you have. You don't need to upload files here — just let us know what exists so your investigator knows what to ask for.">
          <EvidenceCheck
            checked={form.evidence_identity_docs} onChange={v => set('evidence_identity_docs', v)}
            label="Identity documents (ID, proof of address, etc.)"
            notesValue={form.evidence_identity_notes} onNotesChange={v => set('evidence_identity_notes', v)}
            notesPlaceholder="Notes about these documents" />

          <EvidenceCheck
            checked={form.evidence_blockchain} onChange={v => set('evidence_blockchain', v)}
            label="Blockchain evidence (transaction IDs / wallet addresses)"
            notesValue={null} onNotesChange={() => {}}
            notesPlaceholder="" />
          {form.evidence_blockchain && (
            <div className="intake-blockchain-fields">
              <Field label="Transaction IDs (TXIDs)" hint="One per line">
                <textarea rows={3} value={form.evidence_txids} onChange={e => set('evidence_txids', e.target.value)} />
              </Field>
              <Field label="Scammer's wallet address(es)" hint="One per line">
                <textarea rows={2} value={form.evidence_scammer_addresses} onChange={e => set('evidence_scammer_addresses', e.target.value)} />
              </Field>
              <Field label="Your own wallet / exchange address(es) you sent from" hint="One per line">
                <textarea rows={2} value={form.evidence_client_addresses} onChange={e => set('evidence_client_addresses', e.target.value)} />
              </Field>
            </div>
          )}

          <EvidenceCheck
            checked={form.evidence_communications} onChange={v => set('evidence_communications', v)}
            label="Communications (chat logs, emails, call recordings)"
            notesValue={form.evidence_communications_notes} onNotesChange={v => set('evidence_communications_notes', v)}
            notesPlaceholder="What you have and roughly how much of it" />

          <EvidenceCheck
            checked={form.evidence_financial_proof} onChange={v => set('evidence_financial_proof', v)}
            label="Proof of payment (bank/exchange statements, receipts)"
            notesValue={form.evidence_financial_notes} onNotesChange={v => set('evidence_financial_notes', v)}
            notesPlaceholder="Which institution(s) / accounts" />
        </Section>

        <Section number="6" title="Confidentiality & Disclaimer">
          <div className="intake-disclaimer-box">
            <p>
              The information you provide will be used solely for the purpose of investigating
              this incident and, where applicable, supporting a report to law enforcement. It will
              be kept confidential and shared only with your investigating team and, if you've
              filed one, the relevant law enforcement agency. Submitting this form does not
              guarantee recovery of lost funds — blockchain tracing can identify where funds moved,
              but recovery ultimately depends on cooperation from exchanges and, where relevant,
              law enforcement action.
            </p>
          </div>
          <label className="intake-checkbox-row intake-disclaimer-check">
            <input type="checkbox" checked={form.disclaimer_accepted} onChange={e => set('disclaimer_accepted', e.target.checked)} />
            <span>I have read and understood the above, and everything I've entered in this form is accurate to the best of my knowledge.</span>
          </label>
          <Field label="Type your full name to sign">
            <input type="text" value={form.signature_name} onChange={e => set('signature_name', e.target.value)} placeholder="Full legal name" />
          </Field>
        </Section>

        <div className="intake-submit-row">
          <p className="intake-submit-hint">
            Once submitted, you won't be able to edit this form — only your investigator can, from
            here on.
          </p>
          <button type="submit" className="intake-submit-btn" disabled={submitting || locked}>
            {submitting ? 'Submitting…' : 'Submit Intake Form'}
          </button>
        </div>
      </form>
    </div>
  );
}
